import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Minus, Plus, ShieldCheck, ShoppingCart, Trash2, Truck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/contexts/CartContext";
import { analytics } from "@/lib/analytics";
import { toast } from "@/hooks/use-toast";

export function CartDrawer({ close }: { close: () => void }) {
  const { items, removeFromCart, updateQuantity, clearCart, totalItems, totalPrice, maxQuantity } = useCart();
  const navigate = useNavigate();
  const tracked = useRef(false);

  const checkout = () => {
    if (!items.length) {
      toast({ title: "Cart is empty", description: "Add Brain Charge™ first.", variant: "destructive" });
      return;
    }
    if (!tracked.current) {
      analytics.trackBeginCheckout(totalPrice, totalItems);
      tracked.current = true;
    }
    close();
    navigate("/checkout");
  };

  return (
    <SheetContent className="flex h-full w-full flex-col p-0 sm:max-w-md">
      <SheetHeader className="border-b bg-card/50 p-5 text-left">
        <SheetTitle className="flex items-center gap-2 text-lg"><ShoppingCart className="h-5 w-5" />Your Cart {totalItems > 0 && <Badge variant="secondary">{totalItems}</Badge>}</SheetTitle>
        <SheetDescription>Review Brain Charge, adjust quantity, then continue to shipping details.</SheetDescription>
      </SheetHeader>

      {!items.length ? (
        <div className="flex flex-1 flex-col items-center justify-center p-6 text-center text-muted-foreground">
          <ShoppingCart className="mb-4 h-10 w-10 opacity-40" />
          <p className="font-medium text-foreground">Your cart is empty</p>
          <p className="mt-1 text-sm">Add Brain Charge™ to get started.</p>
        </div>
      ) : (
        <>
          <div className="flex-1 space-y-3 overflow-auto p-4 sm:p-6">
            {items.map((item) => (
              <div key={item.product} className="flex items-center gap-3 border-b border-border py-4">
                <div className="min-w-0 flex-1">
                  <h3 className="truncate font-semibold">{item.name}</h3>
                  <p className="text-sm text-muted-foreground">${item.price.toFixed(2)} each</p>
                </div>
                <div className="flex items-center border border-border" aria-label={`${item.name} quantity`}>
                  <Button variant="ghost" size="icon" className="h-11 w-11 rounded-none" onClick={() => updateQuantity(item.product, item.quantity - 1)} aria-label="Decrease quantity"><Minus className="h-4 w-4" /></Button>
                  <span className="w-8 text-center text-sm font-semibold">{item.quantity}</span>
                  <Button variant="ghost" size="icon" className="h-11 w-11 rounded-none" onClick={() => updateQuantity(item.product, item.quantity + 1)} disabled={item.quantity >= maxQuantity} aria-label="Increase quantity"><Plus className="h-4 w-4" /></Button>
                </div>
                <Button variant="ghost" size="icon" className="h-11 w-11 text-muted-foreground hover:text-destructive" onClick={() => removeFromCart(item.product)} aria-label={`Remove ${item.name}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button variant="ghost" size="sm" className="w-full text-muted-foreground" onClick={clearCart}>Clear cart</Button>
          </div>

          <div className="space-y-4 border-t bg-background p-5">
            <div className="flex items-center justify-between"><span className="text-sm text-muted-foreground">Subtotal</span><strong className="text-xl">${totalPrice.toFixed(2)}</strong></div>
            <p className="flex items-start gap-2 text-xs text-muted-foreground"><Truck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />Shipping is selected from your destination on the next page. No order-value shipping threshold.</p>
            <Button className="h-12 w-full text-base font-bold" onClick={checkout}>Checkout</Button>
            <div className="flex justify-center gap-5 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><ShieldCheck className="h-4 w-4 text-primary" />Secure</span>
              <span className="flex items-center gap-1"><CreditCard className="h-4 w-4 text-accent" />Stripe checkout</span>
            </div>
          </div>
        </>
      )}
    </SheetContent>
  );
}